import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { useSuspense, useController } from '@rest-hooks/react';

import ProductResource from 'resources/organization/ProductResource';
import EventResource from 'resources/organization/EventResource';
import OrganizationResource from 'resources/organization/OrganizationResource';

import { defaultOffer, offerCategory, updateCoupons, updatePromoCodes } from 'utils/offersFormHelper';
import { convertPriceFromCents } from 'utils/rewards';
import { priceErrors } from 'utils/pricingsConsts';
import { useError } from 'utils/useErrorController';
import { scrollToTop } from 'utils/scrollToTop';
import { useToast } from 'utils/context/ToastContext';

import Media from './Form/Media';
import Footer from './Form/Footer';
import NewProductModal from './NewProductModal';
import Price from './Form/Price';
import Details from './Form/Details';
import Availability from './Form/Availability';
import SuccessMessage from './Form/SuccessMessage';
import SuccessEmail from './Form/SuccessEmail';

import NotificationList from 'components/NotificationList';
import SeoOptions from 'components/SeoOptions';

import FormSection from 'ui/FormSection';

import { handleValidation } from './handleValidation';
import { createFormData } from './createFormData';

const OffersForm = ({ record={} }) => {

    const { fetch } = useController();
    const history = useHistory();
    const params  = useParams();

    const { handleError, loading, setLoading, validate } = useError();
    const { showToast } = useToast();

    const organizationId = parseInt(params.organizationId);

    const hasRecord = Object.keys(record).length > 0;

    const searchParams = new URLSearchParams(window.location.search);
    const eventId = searchParams.get('eventId');

    const events = useSuspense(EventResource.list(), {organization_id: organizationId, upcoming: true});
    const organization = useSuspense(OrganizationResource.detail(), {id: organizationId});

    const {currency, application_fee_enabled} = organization;

    const initialEventId = hasRecord && record.event_id ? record.event_id : eventId ? eventId : null;
    const initialEventTitle = hasRecord && record.event_title ? record.event_title : eventId ? events.find(item => item.id === +eventId).title : null;
    const initialCategory = hasRecord && record.category ? record.category : eventId ? offerCategory.ticket.value : offerCategory.merch.value;
    const initialFreeValue = hasRecord ? !record.unit_price : false;
    const initialVisibleValue = hasRecord ? record.visible : eventId ? 'always' : 'available';

    const initialOffer = { ...defaultOffer,
                           ...record,
                           unit_price: convertPriceFromCents(record.unit_price),
                           application_fee: convertPriceFromCents(record.application_fee),
                           currency: currency,
                           category: initialCategory,
                           event_id: initialEventId,
                           event_title: initialEventTitle,
                           free: initialFreeValue,
                           visible: initialVisibleValue
                        };

    const [offer, setOffer] = useState(initialOffer);
    const [errors, setErrors] = useState(priceErrors);
    const [openCustomModal, setOpenCustomModal] = useState(null);

    const handleChange = ({target: {name, value}}) => {
        setOffer(currentOffer => {
            let newOffer;

            if (name === "unit_price") {
                const newCoupons = updateCoupons(currentOffer.coupons, value);
                const newPromoCodes = updatePromoCodes(currentOffer.new_promo_codes, value);
                
                newOffer = { ...currentOffer, [name]: value, coupons: newCoupons, new_promo_codes: newPromoCodes };
            } else {
                newOffer = { ...currentOffer, [name]: value };
            }

            return newOffer;
        });
    };

    const handleCheckBoxChange = ({target: {checked, name}}) => {
        let newOffer = { ...offer, [name]: checked };
        setOffer(newOffer);
    };

    const handleSwitch = ({target: {checked}}) => {
        let newOffer = { ...offer, status: checked ? 'active' : 'inactive' };
        setOffer(newOffer);
    };

    const request = record.id ? ProductResource.update() : ProductResource.create();
    const requestBody = record.id ?  {organization_id: organizationId, id: offer.id} : {organization_id: organizationId};

    const handleSubmit = useCallback(
        async (e, offer) => {
          try {
            e.preventDefault();
            validate(offer, handleValidation);

            const formData = createFormData(offer);
            const {id} = await fetch(request, requestBody, formData);

            handleSuccessfulResponse(id);
          } catch (error) {
            handleError(error)
          }
        },
        [fetch],
    );

    const handleSuccessfulResponse = async (id) => {
        await fetch(ProductResource.detail(), { id: id, organization_id: organizationId });
        await fetch(ProductResource.list(), {organization_id: organizationId, active: true});
        await fetch(ProductResource.list(), {organization_id: organizationId});
        await fetch(EventResource.list(), {organization_id: organizationId, upcoming: true});
        setLoading(false);

        if (record.id) {
            showToast('success', `${offer.category} updated successfully`);
            handleRedirect();
        } else {
            setOpenCustomModal(true);
        }
    };

    const handleRedirect = () => {
        let path = `/organizations/${organizationId}/`;

        if (offer?.category === 'ticket') {
            path += offer.event_id ? `events/${offer.event_id}/edit` : 'events';
        } else {
            path += 'perks';
        }

        history.push(path);
    };

    const handleCloseCustomModal = () => {
        setOpenCustomModal(false);
        setOffer({...defaultOffer,
                    category: offer.category,
                    event_id: offer.event_id,
                    event_title: offer.event_title,
                    visible: initialVisibleValue,
                    tag_ids: []
                });
        setErrors(priceErrors);
        scrollToTop();
    };

    const {id, status, title, description, slug, category, notification_list, event_title} = offer;

    const isTicket = category === offerCategory.ticket.value;
    const formTitle = isTicket ? "Ticket Details for " + event_title : "Product Details";

    return (
        <>
            <form onSubmit={ (e) => handleSubmit(e, offer) }>

            <Details
                formTitle={formTitle}
                title={title}
                description={description}
                category={category}
                handleChange={handleChange}
                object={offer}
                onObjectChange={setOffer}
                organizationId={organizationId}
                isTicket={isTicket} />

            {!isTicket &&
                <Media
                    offer={ offer }
                    onOfferChange={ setOffer }
                    loading={ loading }
                    setLoading={ setLoading }
                    onError={ handleError } />
            }

            <Price
                errors={errors}
                onErrorsChange={setErrors}
                offer={offer}
                onOfferChange={setOffer}
                onChange={handleChange}
                currency={currency}
                application_fee_enabled={application_fee_enabled} />

            <Availability
                onChange={handleChange}
                onCheckBoxChange={handleCheckBoxChange}
                object={offer}
                onObjectChange={setOffer} />

            {!isTicket &&
                <SeoOptions
                    slug={slug}
                    onChange={handleChange} />
            }

            <FormSection title={"Purchase Notification"}>
              <NotificationList
                  notification_list={notification_list}
                  onChange={handleChange}
                  context="productForm" />
            </FormSection>

            {/* to reset MarkdownEditor need to force component re-render by changing expandable */}
            <SuccessMessage
                expandable={!openCustomModal}
                product={offer}
                onProductChange={setOffer}
                onChange={handleChange} />

            {/* to reset MarkdownEditor need to force component re-render by changing expandable */}
            <SuccessEmail
                expandable={!openCustomModal}
                product={offer}
                onProductChange={setOffer} />

            <Footer
                id={ id }
                status={ status }
                onChange={ handleSwitch }
                loading={ loading }
                isTicket={ isTicket }
                />
        </form>

        {openCustomModal &&
            <NewProductModal
                open={!!openCustomModal}
                title={offer.title}
                onClose={handleRedirect}
                done={handleCloseCustomModal}
                category={offer.category}
                /> }
        </>
    );
};

OffersForm.propTypes = {
    record: PropTypes.object,
};

export default OffersForm;
